import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useCommons } from "../lib/store";
import { projectById, isStewardOrCo } from "../lib/domain";
import { completeProject } from "../lib/api";

export default function Story() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { data, user, refresh } = useCommons();
  const [what, setWhat] = useState("");
  const [changed, setChanged] = useState("");
  const [learned, setLearned] = useState("");
  const [thanks, setThanks] = useState("");
  const [busy, setBusy] = useState(false);

  if (!data) return <div className="c-load">Loading…</div>;
  const project = projectById(data.projects, id);
  if (!project) return <div className="c-load">Not found.</div>;
  const iAmSteward = user && isStewardOrCo(user.personId, project);
  const composing = sp.get("compose") === "1" && iAmSteward && project.status !== "completed";

  const save = async () => {
    if (busy || !what.trim() || !changed.trim() || !learned.trim()) return;
    setBusy(true);
    try {
      await completeProject(project.id, {
        what: what.trim(), changed: changed.trim(), learned: learned.trim(),
        thanks: thanks.trim() || undefined,
        photos: [project.emoji],
      });
      await refresh();
      toast.success("Story saved. Thank you.");
      nav(`/story/${project.id}`, { replace: true });
    } finally { setBusy(false); }
  };

  if (composing) {
    return (
      <div style={{ paddingBottom: 40 }}>
        <div className="c-detail-topbar">
          <button className="c-icon-btn" onClick={() => nav(-1)}><ArrowLeft size={20} /></button>
          <div />
        </div>
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="c-eyebrow">Close the loop</div>
          <h1 className="c-h1">Tell the story.</h1>
          <div className="c-body">A few sentences is plenty. What happened? What changed? What did you learn?</div>

          <div className="c-label">What happened?</div>
          <textarea className="c-input c-input-tall" value={what} onChange={(e) => setWhat(e.target.value)} data-testid="story-what" />

          <div className="c-label">What changed?</div>
          <textarea className="c-input c-input-tall" value={changed} onChange={(e) => setChanged(e.target.value)} data-testid="story-changed" />

          <div className="c-label">What did we learn?</div>
          <textarea className="c-input c-input-tall" value={learned} onChange={(e) => setLearned(e.target.value)} data-testid="story-learned" />

          <div className="c-label">Thanks (optional)</div>
          <textarea className="c-input" value={thanks} onChange={(e) => setThanks(e.target.value)} data-testid="story-thanks" />

          <button className="c-btn c-btn-primary" onClick={save} disabled={busy} data-testid="story-save">
            {busy ? "Saving…" : "Save story & complete"}
          </button>
        </div>
      </div>
    );
  }

  const story = project.story;
  if (!story) {
    return (
      <div style={{ padding: 20 }}>
        <button className="c-icon-btn" onClick={() => nav(-1)}><ArrowLeft size={20} /></button>
        <div className="c-body" style={{ marginTop: 20 }}>No story yet.</div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 40 }}>
      <div className="c-detail-topbar">
        <button className="c-icon-btn" onClick={() => nav(-1)}><ArrowLeft size={20} /></button>
        <div />
      </div>
      <div className="c-story-hero">
        <div style={{ fontSize: 42 }}>{project.emoji}</div>
        <div className="c-eyebrow" style={{ color: "var(--c-brand-tertiary)", marginTop: 8 }}>Completed</div>
        <h1 className="c-h1" style={{ color: "var(--c-on-surface-inverse)", marginTop: 6 }}>{project.title}</h1>
        <div className="c-detail-purpose" style={{ color: "#D9D3C6", marginTop: 8 }}>{project.purpose}</div>
      </div>

      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 20 }}>
        <StorySection title="What happened" body={story.what} />
        <StorySection title="What changed" body={story.changed} />
        <StorySection title="What we learned" body={story.learned} />
        {story.thanks ? <StorySection title="Thanks" body={story.thanks} /> : null}

        {story.photos?.length ? (
          <div>
            <div className="c-eyebrow">Photos</div>
            <div style={{ display: "flex", gap: 8, marginTop: 8, fontSize: 32 }}>
              {story.photos.map((p, i) => <span key={i}>{p}</span>)}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StorySection({ title, body }) {
  return (
    <div>
      <div className="c-eyebrow">{title}</div>
      <div className="c-detail-purpose" style={{ marginTop: 6 }}>{body}</div>
    </div>
  );
}
